import { Flex, Skeleton, Text } from "@chakra-ui/react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { FiPieChart } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { ContractStatusDto, useListContractReportsQuery } from "../../../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../../../common/auth/useLoggedInWithOrgContextState";
import { base64Encode } from "../../../../common/utils/base64";
import { urls } from "../../../../urls";
import { EXPIRY_WINDOWS, expiryWindowQuery, type ExpiryWindowKey } from "../contractExpiryWindows";
import { useHomeLabel } from "../useHomeLabel";
import { DoughnutBreakdown } from "./DoughnutBreakdown";
import { PanelBody, PanelCard, PanelHeader } from "./HomePanel";

/** Sooner is louder: the band you must act on this month reads first. */
const WINDOW_COLOR: Record<ExpiryWindowKey, string> = {
  month: "smChartExpiryMonth",
  quarter: "smChartExpiryQuarter",
  halfYear: "smChartExpiryHalfYear",
  year: "smChartExpiryYear",
};

export const ContractExpiryPanel = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const homeLabel = useHomeLabel();
  const authState = useLoggedInWithOrgContextState();

  const queries = useMemo(
    () =>
      EXPIRY_WINDOWS.map((window) => ({
        window,
        filters: expiryWindowQuery(window, authState.selectedOrg.id),
      })),
    [authState.selectedOrg.id]
  );

  // `limit: 0` asks for the count only — the bands are counted in the database, never by pulling
  // contracts across and tallying them here.
  const results = [
    useListContractReportsQuery({ q: base64Encode(JSON.stringify({ ...queries[0].filters, limit: 0 })) }),
    useListContractReportsQuery({ q: base64Encode(JSON.stringify({ ...queries[1].filters, limit: 0 })) }),
    useListContractReportsQuery({ q: base64Encode(JSON.stringify({ ...queries[2].filters, limit: 0 })) }),
    useListContractReportsQuery({ q: base64Encode(JSON.stringify({ ...queries[3].filters, limit: 0 })) }),
  ];

  const counts = results.map((result) => result.data?.count ?? 0);
  const total = counts.reduce((sum, count) => sum + count, 0);
  const isLoading = results.some((result) => result.isLoading);
  const isError = results.some((result) => result.isError);

  const openWindow = (index: number) =>
    navigate(`${urls.v2.contracts.viewAll}?q=${base64Encode(JSON.stringify(queries[index].filters))}`);

  // Composed rather than the one-shot HomePanel: an organization with nothing expiring still has
  // agreements that never expire, and the empty state must not take that figure down with it.
  return (
    <PanelCard>
      <PanelHeader titleKey="Agreements expiring" icon={FiPieChart} count={total} seeAllTo={urls.v2.contracts.active} />
      <PanelBody
        isLoading={isLoading}
        isError={isError}
        isEmpty={total === 0}
        emptyKey="No agreements expire in the next year"
      >
        <DoughnutBreakdown
          slices={EXPIRY_WINDOWS.map((window, index) => ({
            key: window.key,
            label: homeLabel(window.labelKey),
            colorToken: WINDOW_COLOR[window.key],
            count: counts[index],
          }))}
          totalLabel={t("Contracts")}
          onSelect={openWindow}
        />
      </PanelBody>
      <NoEndDateFigure />
    </PanelCard>
  );
};

/**
 * The agreements with nothing scheduled to stop them — the ones the chart above cannot show, having
 * no expiry to fall into any band. It sits under the chart for exactly that reason, and stays put
 * when the chart itself has nothing to draw.
 */
const NoEndDateFigure = () => {
  const navigate = useNavigate();
  const homeLabel = useHomeLabel();
  const authState = useLoggedInWithOrgContextState();

  const filters = {
    owningOrganizationId: authState.selectedOrg.id,
    statuses: [ContractStatusDto.Active],
    hasNoEndDate: true,
  };
  const { data, isLoading } = useListContractReportsQuery({
    q: base64Encode(JSON.stringify({ ...filters, limit: 0 })),
  });

  return (
    <Flex
      as="button"
      type="button"
      direction="column"
      align="flex-start"
      w="full"
      px="4"
      py="3"
      borderTop="1px solid"
      borderColor="smBorder"
      textAlign="left"
      _hover={{ bg: "smBackgroundSecondary" }}
      onClick={() => navigate(`${urls.v2.contracts.viewAll}?q=${base64Encode(JSON.stringify(filters))}`)}
    >
      {isLoading ? (
        <Skeleton h="30px" w="3rem" my="1" />
      ) : (
        <Text fontSize="2xl" color="smText" lineHeight="1.2">
          {data?.count ?? 0}
        </Text>
      )}
      <Text fontSize="sm" color="smText">
        {homeLabel("Agreements without an end date")}
      </Text>
    </Flex>
  );
};
